import React from 'react';
import { testId } from '../../../../utils/testIds';
import SearchableSelectField from '../SearchableSelectField';
import { RENDER_MODE, UNSELECTED_OPTION_VALUE } from '../../constants';

const normalize = str => String(str ?? '').trim().toLowerCase();
const isSameOption = (a, b) => normalize(a) === normalize(b);
const findOption = (val, options) => options.find(o => isSameOption(o.value, val));

function SelectField({
  config, value, onChange, onBlur,
  disabled, required, options = [], error, multi = false, renderMode,
}) {
  if (!multi && config?.fieldSettings?.searchable) {
    return (
      <SearchableSelectField
        config={config}
        value={value}
        onChange={onChange}
        onBlur={onBlur}
        disabled={disabled}
        required={required}
        options={options}
        error={error}
      />
    );
  }

  if (renderMode === RENDER_MODE.CARD || renderMode === RENDER_MODE.GRID) {
    const displayLabel = getDisplayLabel(value, options);
    return (
      <span className="field-engine-select-value">
        {displayLabel || <span className="field-engine-empty">—</span>}
      </span>
    );
  }

  if (multi) {
    return (
      <MultiSelect
        config={config}
        value={value}
        onChange={onChange}
        onBlur={onBlur}
        disabled={disabled}
        required={required}
        options={options}
        error={error}
      />
    );
  }

  return (
    <select
      id={config.fieldKey}
      data-test={testId('field', config.fieldKey, 'input')}
      name={config.fieldKey}
      className={`field-engine-select${error ? ' field-engine-select--error' : ''}${disabled ? ' disabled' : ''}`}
      value={findOption(value, options)?.value ?? UNSELECTED_OPTION_VALUE}
      onChange={e => onChange(e.target.value)}
      onBlur={onBlur}
      disabled={disabled}
      required={required}
      aria-required={required}
      aria-invalid={!!error}
    >
      {options.map(opt => (
        <option key={opt.value} value={opt.value}>
          {opt.label}
        </option>
      ))}
    </select>
  );
}

function MultiSelect({ config, value, onChange, onBlur, disabled, required, options, error }) {
  const selectedValues = Array.isArray(value) ? value : (value ? [value] : []);

  function handleChange(optValue) {
    let next;
    if (selectedValues.includes(optValue)) {
      next = selectedValues.filter(v => v !== optValue);
    } else {
      next = [...selectedValues, optValue];
    }
    onChange(next);
  }

  return (
    <div
      className={`field-engine-multiselect${error ? ' field-engine-multiselect--error' : ''}${disabled ? ' disabled' : ''}`}
      role="group"
      data-test={testId('field', config.fieldKey, 'options')}
      aria-required={required}
      aria-invalid={!!error}
    >
      {options.map(opt => (
        <label key={opt.value} className="field-engine-multiselect-option">
          <input
            type="checkbox"
            value={opt.value}
            checked={selectedValues.some(v => isSameOption(v, opt.value))}
            onChange={() => handleChange(opt.value)}
            onBlur={onBlur}
            disabled={disabled}
          />
          <span>{opt.label}</span>
        </label>
      ))}
    </div>
  );
}

function getDisplayLabel(value, options) {
  if (!value || value === UNSELECTED_OPTION_VALUE) return '';

  if (Array.isArray(value)) {
    return value
      .map(v => findOption(v, options)?.label ?? v)
      .filter(Boolean)
      .join(', ');
  }

  return findOption(value, options)?.label ?? value;
}

export default SelectField;
